import React, { useState, useEffect, useRef, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, addDoc, query, onSnapshot, orderBy, serverTimestamp } from 'firebase/firestore';

// Define mandatory global variables provided by the Canvas environment
// eslint-disable-next-line no-undef
const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
// eslint-disable-next-line no-undef
const firebaseConfig = typeof __firebase_config !== 'undefined' ? JSON.parse(__firebase_config) : {
    // Fallback Firebase config for development
    apiKey: "demo-api-key",
    authDomain: "demo-project.firebaseapp.com",
    projectId: "demo-project",
    storageBucket: "demo-project.appspot.com",
    messagingSenderId: "123456789",
    appId: "demo-app-id"
};
// eslint-disable-next-line no-undef
const initialAuthToken = typeof __initial_auth_token !== 'undefined' ? __initial_auth_token : null;

// Reusable FeatureCard Component
const FeatureCard = ({ emoji, title, description, emojiBgColor, emojiBorderColor }) => (
    <div className="bg-white rounded-none p-6 flex flex-col items-center text-center border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,1)]">
        <div className={`text-4xl mb-4 p-2 bg-stone-100 border-2 ${emojiBorderColor} ${emojiBgColor}`}>
            {emoji}
        </div>
        <h4 className="text-xl font-bold text-gray-900 mb-2">{title}</h4>
        <p className="text-gray-700 text-sm">{description}</p>
    </div>
);

// Reusable PlanCard Component
const PlanCard = ({ plan, price, description, features, buttonText, isPopular = false }) => {
    const textColor = isPopular ? 'text-white' : 'text-gray-900';
    const bgColor = isPopular ? 'bg-blue-600' : 'bg-white';
    const borderColor = isPopular ? 'border-white' : 'border-black';
    const shadowColor = isPopular ? 'shadow-[8px_8px_0px_rgba(0,0,0,1)]' : 'shadow-[6px_6px_0px_rgba(0,0,0,1)]';
    const buttonBg = isPopular ? 'bg-black hover:bg-gray-800' : 'bg-gray-300 hover:bg-gray-400';
    const buttonShadow = isPopular ? 'shadow-[4px_4px_0px_rgba(255,255,255,1)]' : 'shadow-[4px_4px_0px_rgba(0,0,0,1)]';
    const priceColor = isPopular ? 'text-blue-200' : 'text-gray-600';
    const featureCheckColor = isPopular ? 'text-green-300' : 'text-green-700';

    return (
        <div className={`${bgColor} rounded-none ${shadowColor} p-8 flex flex-col border-4 border-black relative`}>
            {isPopular && (
                <div className="absolute top-0 left-1/2 -translate-y-1/2 -translate-x-1/2 bg-red-600 text-white text-xs font-extrabold py-1 px-4 rounded-none shadow-[4px_4px_0px_rgba(0,0,0,1)] whitespace-nowrap border-2 border-black">
                    MOST POPULAR
                </div>
            )}
            <div className="text-center mb-6 mt-4">
                <div className={`${textColor} text-4xl mb-3`}>{plan === 'Starter' ? '⚡' : plan === 'Pro' ? '👑' : '💎'}</div>
                <h3 className={`text-2xl font-bold ${textColor} mb-2`}>{plan}</h3>
                <p className={`text-4xl font-extrabold ${textColor}`}>
                    ${price}<span className={`${priceColor} text-lg font-medium`}>/month</span>
                </p>
                <p className={`${isPopular ? 'text-blue-100' : 'text-gray-700'} text-sm mt-2`}>{description}</p>
            </div>
            <ul className={`${isPopular ? 'text-white' : 'text-gray-800'} space-y-3 flex-grow mb-8`}>
                {features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                        <svg className={`w-5 h-5 ${featureCheckColor} mr-2`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg> {feature}
                    </li>
                ))}
            </ul>
            <button 
                onClick={() => {
                    if (plan === 'Starter') {
                        alert('🎉 Welcome to Ad Kitchen Starter! You now have 10 free credits to start creating amazing ads!');
                    } else if (plan === 'Pro') {
                        alert('🚀 Welcome to Ad Kitchen Pro! You now have 300 credits and access to premium features!');
                    } else if (plan === 'Premium') {
                        alert('💎 Welcome to Ad Kitchen Premium! You now have 900 credits and full access to all features!');
                    }
                }}
                className={`w-full ${buttonBg} text-white font-extrabold py-3 px-6 rounded-none ${buttonShadow} hover:bg-opacity-80 transition-colors duration-100 border-2 ${borderColor} active:translate-x-0.5 active:translate-y-0.5 active:shadow-none`}
            >
                {buttonText}
            </button>
        </div>
    );
};

// Main App Component
const App = () => {
    // State variables for Firebase and user authentication
    const [db, setDb] = useState(null);
    const [userId, setUserId] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);

    // State variables for image generation
    const [prompt, setPrompt] = useState('');
    const [generatedImageUrl, setGeneratedImageUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeMode, setActiveMode] = useState('text-to-image'); // 'image-to-image' or 'text-to-image'
    const [uploadedImagePreview, setUploadedImagePreview] = useState(null); // Base64 for preview
    const [uploadedImageFile, setUploadedImageFile] = useState(null); // File object for potential upload to other models if needed

    // State for user's saved images
    const [savedImages, setSavedImages] = useState([]);

    // Ref for controlling scroll to generated image
    const generatedImageRef = useRef(null);
    const fileInputRef = useRef(null);

    // Aspect Ratio state (purely for UI visual selection, not affecting Gemini API for now)
    const [aspectRatio, setAspectRatio] = useState('1:1');

    // State for price calculator
    const [expectedGenerations, setExpectedGenerations] = useState(50); // Default to 50 generations
    const [needsProFeatures, setNeedsProFeatures] = useState(false); // Checkbox for pro features if applicable

    // --- Firebase Initialization and Authentication ---
    useEffect(() => {
        try {
            const app = initializeApp(firebaseConfig);
            const authInstance = getAuth(app);
            const firestoreInstance = getFirestore(app);

            setDb(firestoreInstance);

            const unsubscribe = onAuthStateChanged(authInstance, async (user) => {
                if (user) {
                    setUserId(user.uid);
                } else {
                    try {
                        if (initialAuthToken) {
                            await signInWithCustomToken(authInstance, initialAuthToken);
                        } else {
                            await signInAnonymously(authInstance);
                        }
                        setUserId(authInstance.currentUser?.uid || crypto.randomUUID());
                    } catch (error) {
                        console.error("Firebase Auth Error:", error);
                        // Use fallback user ID for demo purposes
                        setUserId(crypto.randomUUID());
                    }
                }
                setIsAuthReady(true);
            });

            return () => unsubscribe();
        } catch (error) {
            console.error("Firebase initialization error:", error);
            // Set fallback values for demo mode
            setUserId(crypto.randomUUID());
            setIsAuthReady(true);
        }
    }, []);

    // --- Firestore Data Listener (for saved images) ---
    useEffect(() => {
        if (!db || !userId || !isAuthReady) return;

        try {
            const imagesCollectionRef = collection(db, `artifacts/${appId}/users/${userId}/generated_images`);
            const q = query(imagesCollectionRef, orderBy('timestamp', 'desc'));

            const unsubscribe = onSnapshot(q, (snapshot) => {
                const images = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setSavedImages(images);
            }, (err) => {
                console.error("Error fetching saved images:", err);
                // Don't show error for demo mode
            });

            return () => unsubscribe();
        } catch (error) {
            console.error("Firestore setup error:", error);
            // Continue without saved images in demo mode
        }
    }, [db, userId, isAuthReady]);

    // --- Image Generation Logic (OpenAI DALL-E API Call) ---
    // Expands a basic user prompt into a highly detailed one for better generations
    const enhancePrompt = (basicPrompt, currentAspectRatio) => {
        const detailedPrompt = `
Core Scene Description
Create a vibrant, inspiring advertisement image featuring ${basicPrompt} in [SETTING] during [TIME OF DAY/LIGHTING CONDITION]. [PRODUCT PLACEMENT DESCRIPTION].

Character/Subject Details
Primary Subject: [Age range, physical characteristics, demeanor]
Expression: [Emotional state, facial expression details]
Attire: [Clothing style, colors, specific garments]
[Item 1]
[Item 2]
[Item 3]
[Additional styling details]
Pose: [Body position, action, movement description]

Product Specifications
Design: [Product appearance, style characteristics]
Color: [Color scheme, finish details]
Features: [Key visible features, functionality elements]
Branding: [Logo placement area, brand visibility notes]
Integration: [How product fits naturally into scene]

Environment/Setting
Location: [Primary setting description]
Surface/Ground: [Floor, ground, surface details]
Background Elements: [Secondary environmental features]
Atmosphere: [Overall environmental mood and feel]
Context: [Situational details that support the narrative]

Lighting and Mood
Time: [Lighting conditions, time of day]
Shadows: [Shadow quality, direction, intensity]
Overall tone: [Emotional atmosphere, energy level]
Color palette: [Primary and accent colors, mood description]

Technical Specifications
Composition: [Framing, positioning, visual balance]
Depth of field: [Focus areas, blur effects]
Angle: [Camera perspective, viewpoint]
Movement: [Motion capture, dynamic elements]
Resolution: High-resolution, suitable for large format printing and digital use
Aspect Ratio: ${currentAspectRatio}

Marketing Message Elements
Lifestyle: [Target lifestyle, demographic characteristics]
Values: [Key values and aspirations being promoted]
Emotional appeal: [Primary emotions to evoke]
Target demographic: [Specific audience details]

Additional Details
Conditions: [Environmental conditions, weather, etc.]
Season: [Time of year, seasonal elements]
Context Elements: [Supporting details that enhance believability]
Authenticity: [Style approach - natural vs. staged]

Text Space Considerations
[Area 1] for [text purpose]
[Area 2] for [text purpose]
[Negative space considerations]
[Brand/logo placement requirements]

This image should convey [PRIMARY MESSAGE] and communicate [SECONDARY MESSAGES] while effectively showcasing [PRODUCT/SERVICE] in [IDEAL CONTEXT].
        `;
        return detailedPrompt;
    };

    const generateImage = async () => {
        if (!prompt.trim()) {
            setError('Please enter a prompt to generate an image.');
            return;
        }
        if (activeMode === 'image-to-image' && !uploadedImageFile) {
            setError('Please upload a reference image for Image to Image mode.');
            return;
        }

        setIsLoading(true);
        setGeneratedImageUrl('');
        setError('');

        try {
            // Call backend API instead of OpenAI directly
            const enhancedPromptText = enhancePrompt(prompt, aspectRatio);
            let fullPromptText = enhancedPromptText;
            
            if (activeMode === 'image-to-image' && uploadedImageFile) {
                fullPromptText = `${enhancedPromptText}\nUse the attached reference image as creative guidance.`;
            }

            // Prepare request payload
            const requestPayload = {
                type: 'image',
                prompt: fullPromptText,
                mode: activeMode
            };

            // Add image data if in image-to-image mode
            if (activeMode === 'image-to-image' && uploadedImagePreview) {
                requestPayload.imageData = uploadedImagePreview;
            }

            // Call our backend API
            const response = await fetch('/api/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestPayload)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.result) {
                setGeneratedImageUrl(data.result);
                setTimeout(() => {
                    generatedImageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            } else {
                setError('Failed to generate image. Please try a different prompt.');
                console.error('API response error:', data);
            }
        } catch (err) {
            console.error('API call failed:', err);
            setError(`Error generating image: ${err.message}. Please try again later.`);
        } finally {
            setIsLoading(false);
        }
    };

    // --- Handle Image Upload ---
    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setError('File size exceeds 5MB limit.');
                setUploadedImagePreview(null);
                setUploadedImageFile(null); // Set to null on error
                return;
            }
            const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                setError('Only JPEG, PNG, or WebP images are allowed.');
                setUploadedImagePreview(null);
                setUploadedImageFile(null); // Set to null on error
                return;
            }

            setError('');
            setUploadedImageFile(file); // Correctly set the file object
            const reader = new FileReader();
            reader.onloadend = () => {
                setUploadedImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setUploadedImagePreview(null);
            setUploadedImageFile(null); // Set to null if no file is selected
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            handleImageUpload({ target: { files: [files[0]] } });
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    // --- Save Image to Firestore Gallery ---
    const saveImageToGallery = async () => {
        if (!generatedImageUrl) {
            setError("Cannot save. No image generated.");
            return;
        }

        if (!db || !userId) {
            // Demo mode - just show success message
            console.log("Demo mode: Image would be saved to gallery!");
            return;
        }

        try {
            await addDoc(collection(db, `artifacts/${appId}/users/${userId}/generated_images`), {
                prompt: prompt,
                imageUrl: generatedImageUrl,
                aspectRatio: aspectRatio,
                mode: activeMode,
                timestamp: serverTimestamp()
            });
            console.log("Image saved to gallery!");
        } catch (e) {
            console.error("Error adding document: ", e);
            setError("Failed to save image to gallery.");
        }
    };

    // --- Download Generated Image ---
    const downloadImage = () => {
        if (generatedImageUrl) {
            const link = document.createElement('a');
            link.href = generatedImageUrl;
            link.download = `ad-kitchen-${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // --- Custom Alert/Message Box Function ---
    const [copyMessage, setCopyToClipboardMessage] = useState('');
    const copyToClipboardMessage = (message) => {
        setCopyToClipboardMessage(message);
        setTimeout(() => setCopyToClipboardMessage(''), 3000);
    };

    const copyPromptToClipboard = () => {
        if (prompt) {
            document.execCommand('copy');
            copyToClipboardMessage('Prompt copied to clipboard!');
        }
    };

    // Example Prompts for "Try these examples"
    const examplePrompts = [
        'A majestic dragon flying over a medieval castle at sunset, highly detailed, fantasy art',
        'Modern minimalist living room with natural lighting, indoor plants, and comfortable sofa, architectural photography',
        'Abstract digital art with vibrant neon colors, flowing lines, and glowing particles, futuristic'
    ];

    // --- Price Calculator Logic (Memoized) ---
    const { adKitchenCost, adCreativeAICost, savings } = useMemo(() => {
        let adKitchenCost = 0;
        let adCreativeAICost = 0;

        // Ad Kitchen Pricing (based on your pricing plans)
        if (expectedGenerations <= 10) {
            adKitchenCost = 0; // Starter Plan
        } else if (expectedGenerations <= 300) {
            adKitchenCost = 9.99; // Pro Plan
        } else { // > 300 credits
            adKitchenCost = 27.90; // Premium Plan
        }

        // Ad Creative AI Pricing (competitor - higher prices)
        if (expectedGenerations <= 10) {
            adCreativeAICost = 19.00; // Even for low usage, they charge
        } else if (expectedGenerations <= 300) {
            adCreativeAICost = 79.00;
        } else { // > 300 credits
            adCreativeAICost = 199.00;
        }
        
        // Add a flat fee for "PRO features" if checked for both
        if (needsProFeatures) {
            adKitchenCost += 5;
            adCreativeAICost += 20;
        }

        const savings = adCreativeAICost - adKitchenCost;

        return { adKitchenCost, adCreativeAICost, savings };
    }, [expectedGenerations, needsProFeatures]); // Dependencies for useMemo

    // Emoji Logic based on generations
    const getAdKitchenEmoji = (generations) => {
        if (generations <= 10) return '😊'; // Starter
        if (generations <= 300) return '😄'; // Pro
        return '😁'; // Premium
    };

    // Updated Emoji Logic for Ad Creative AI: sadder as generations increase
    const getAdCreativeAIEmoji = (generations) => {
        if (generations <= 10) return '🙂'; // Least sad (at low usage, less pain)
        if (generations <= 300) return '🙁'; // A bit sad (mid-range usage, cost adds up)
        return '😩'; // Very sad/frustrated (high usage, significant cost difference)
    };

    const adKitchenEmoji = getAdKitchenEmoji(expectedGenerations);
    const adCreativeAIEmoji = getAdCreativeAIEmoji(expectedGenerations);

    return (
        <div className="min-h-screen bg-[#f5ece0] p-4 sm:p-6 lg:p-8 flex flex-col items-center justify-center font-inter">
                         {/* Demo Mode Banner */}
             {!db && (
                 <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-black text-center py-2 font-bold z-50 border-b-4 border-black shadow-[4px_4px_0px_rgba(0,0,0,1)]">
                     🎭 DEMO MODE - Firebase not configured. Gemini API key needed for image generation.
                 </div>
             )}
            
            {/* Top Introductory Section (Seamless Block) */}
            <div className="w-full max-w-7xl mx-auto p-8 bg-[#f5ece0] rounded-none flex flex-col items-center justify-center text-center mb-12 relative overflow-hidden">
                <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-4 leading-tight">
                    The most affordable <br className="sm:hidden" /> <span className="text-purple-800">Ad editor</span> alternative
                </h1>
                <p className="text-lg sm:text-xl text-gray-800 mb-6 max-w-3xl">
                    Ad Kitchen is your all-in-one ad creation tool that provides <b className="text-blue-900">UNLIMITED</b> ad concepts and variations for <b className="text-blue-900">FREE</b>.
                    You can easily generate compelling ad creatives, analyze performance, optimize campaigns, and much more! 🤯
                </p>
                <button 
                    onClick={() => {
                        // Simulate account creation - in a real app this would open a signup modal or redirect
                        alert('🎉 Welcome to Ad Kitchen! Your free account has been created. You now have access to unlimited ad generations!');
                    }}
                    className="bg-blue-700 text-white font-extrabold py-3 px-8 rounded-none border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,1)] hover:bg-blue-800 transition-all duration-100 transform hover:scale-[1.01] text-lg mb-6 active:translate-x-1 active:translate-y-1 active:shadow-none"
                >
                    Create free account
                </button>
                <div className="flex items-center justify-center space-x-6 text-gray-800 font-bold text-base sm:text-lg mb-8">
                    <span className="flex items-center p-2 bg-stone-100 border-2 border-black shadow-[4px_4px_0px_rgba(0,0,0,1)]">
                        <svg className="w-5 h-5 text-green-700 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg> Unlimited creations
                    </span>
                    <span className="flex items-center p-2 bg-stone-100 border-2 border-black shadow-[4px_4px_0px_rgba(0,0,0,1)]">
                        <svg className="w-5 h-5 text-green-700 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg> No credit card required
                    </span>
                </div>
                <div className="bg-stone-100 p-4 rounded-none border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,1)] max-w-lg w-full">
                    <p className="text-gray-800 text-lg font-bold italic mb-2">
                        "Ad Kitchen is an absolute joy to use."
                    </p>
                    <div className="flex items-center justify-center">
                        <img src="https://placehold.co/48x48/000000/FFFFFF?text=P" alt="User profile" className="rounded-none w-12 h-12 mr-3 border-2 border-black" />
                        <div className="text-gray-900 text-sm">
                            <p className="font-extrabold">Pieter Levels</p>
                            <p>Founder Nomadlist and RemoteOK</p>
                            <p className="text-gray-700">400k+ Followers on Twitter</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main App Container (Retains Border for Clarity of Core Functionality) */}
            <div className="max-w-7xl w-full bg-white rounded-none border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,1)] overflow-hidden md:flex mb-12">
                {/* Left Panel - Prompt Engine */}
                <div className="p-6 md:p-8 flex-1 border-b-4 md:border-b-0 md:border-r-4 border-black bg-stone-50 relative">
                    <h1 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                        <span className="text-purple-700 text-3xl mr-2">🚀</span> Prompt Engine
                    </h1>
                    <p className="text-gray-700 text-sm mb-6">Transform your image with AI-powered editing</p>

                    {/* Mode Toggle */}
                    <div className="flex bg-stone-100 rounded-none border-2 border-black p-1 mb-8">
                        <button
                            onClick={() => setActiveMode('image-to-image')}
                            className={`flex-1 py-2 px-4 rounded-none text-sm font-semibold transition-colors duration-200 border-2 border-transparent ${
                                activeMode === 'image-to-image' ? 'bg-blue-600 text-white shadow-[4px_4px_0px_rgba(0,0,0,1)]' : 'text-gray-700 hover:bg-stone-200'
                            } active:translate-x-0.5 active:translate-y-0.5 active:shadow-none`}
                        >
                            Image to Image
                        </button>
                        <button
                            onClick={() => setActiveMode('text-to-image')}
                            className={`flex-1 py-2 px-4 rounded-none text-sm font-semibold transition-colors duration-200 border-2 border-transparent ${
                                activeMode === 'text-to-image' ? 'bg-blue-600 text-white shadow-[4px_4px_0px_rgba(0,0,0,1)]' : 'text-gray-700 hover:bg-stone-200'
                            } active:translate-x-0.5 active:translate-y-0.5 active:shadow-none`}
                        >
                            Text to Image
                        </button>
                    </div>

                    {/* Reference Image Upload (conditionally rendered) */}
                    {activeMode === 'image-to-image' && (
                        <div className="mb-8">
                            <label className="block text-gray-700 text-sm font-semibold mb-2 flex items-center">
                                <span className="text-blue-600 text-xl mr-1">📸</span> Reference Image
                            </label>
                            <div
                                className="border-4 border-dashed border-black rounded-none p-6 text-center text-gray-500 cursor-pointer hover:border-blue-500 transition-colors duration-200 shadow-[6px_6px_0px_rgba(0,0,0,1)]"
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onClick={() => fileInputRef.current.click()}
                            >
                                {uploadedImagePreview ? (
                                    <img src={uploadedImagePreview} alt="Uploaded Preview" className="max-h-48 mx-auto rounded-none border-2 border-black mb-2 shadow-md" />
                                ) : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                        <p className="mt-2 text-sm font-bold text-gray-800">Drop an image here or click to upload</p>
                                        <p className="text-xs text-gray-600">JPEG, PNG, or WebP • Max 5MB</p>
                                    </>
                                )}
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleImageUpload}
                                    className="hidden"
                                    accept=".jpeg,.jpg,.png,.webp"
                                />
                            </div>
                            {activeMode === 'image-to-image' && (
                                <p className="text-orange-600 text-xs mt-2 text-center border-2 border-orange-600 p-1">
                                    Note: For true image-to-image editing, a different AI model (e.g., Gemini Flash with image input) would be used.
                                    Here, your text prompt will *guide* the remix of the uploaded image's concept.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Main Prompt */}
                    <div className="mb-6">
                        <label htmlFor="promptInput" className="block text-gray-700 text-sm font-semibold mb-2 flex items-center">
                            <span className="text-amber-600 text-xl mr-1">💡</span> Main Prompt
                        </label>
                        <textarea
                            id="promptInput"
                            className="w-full p-3 bg-stone-100 border-4 border-black rounded-none text-gray-900 placeholder-gray-500 resize-y focus:ring-blue-500 focus:border-blue-500 shadow-[4px_4px_0px_rgba(0,0,0,1)]"
                            rows="4"
                            placeholder="A futuristic city powered by nano technology, golden hour lighting, ultra detailed..."
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                        ></textarea>
                        {copyMessage && (
                            <div className="text-green-700 text-sm mt-2 text-center border-2 border-green-700 p-1">
                                {copyMessage}
                            </div>
                        )}
                        <button
                            onClick={copyPromptToClipboard}
                            className="mt-2 ml-auto text-gray-700 hover:text-gray-900 text-xs px-3 py-1 rounded-none border-2 border-black hover:bg-stone-200 transition-colors duration-200 shadow-[2px_2px_0px_rgba(0,0,0,1)] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
                        >
                            <span className="text-xs mr-1">📋</span> Copy
                        </button>
                    </div>

                    {/* Aspect Ratio */}
                    <div className="mb-8">
                        <label className="block text-gray-700 text-sm font-semibold mb-2 flex items-center">
                            <span className="text-green-600 text-xl mr-1">📐</span> Aspect Ratio
                        </label>
                        <div className="flex space-x-3">
                            {['1:1', '16:9', '9:16'].map(ratio => (
                                <button
                                    key={ratio}
                                    onClick={() => setAspectRatio(ratio)}
                                    className={`flex-1 py-2 px-4 rounded-none font-semibold text-sm transition-colors duration-200 border-2 border-black ${
                                        aspectRatio === ratio ? 'bg-blue-600 text-white shadow-[4px_4px_0px_rgba(0,0,0,1)]' : 'bg-stone-100 text-gray-700 hover:bg-stone-200 shadow-[4px_4px_0px_rgba(0,0,0,1)]'
                                    } active:translate-x-0.5 active:translate-y-0.5 active:shadow-none`}
                                >
                                    {ratio}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Generate Button */}
                    <div className="mb-6">
                        <p className="text-center text-amber-600 font-bold text-sm mb-3">Powered by Google Gemini AI via secure backend • Cost: 2 credits per generation</p>
                        <button
                            onClick={generateImage}
                            disabled={isLoading}
                            className="w-full bg-gradient-to-r from-blue-700 to-indigo-800 text-white font-bold py-3 px-6 rounded-none border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,1)] hover:from-blue-800 hover:to-indigo-900 transition-all duration-100 transform hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-lg active:translate-x-1 active:translate-y-1 active:shadow-none"
                        >
                            {isLoading ? (
                                <>
                                    <span className="loading-spinner mr-3"></span> Generating...
                                </>
                            ) : (
                                'Generate Now'
                            )}
                        </button>
                    </div>

                    {error && (
                        <div className="mt-4 p-3 bg-red-600 border-4 border-black text-white rounded-none text-sm text-center shadow-[4px_4px_0px_rgba(0,0,0,1)]">
                            {error}
                        </div>
                    )}

                    {/* Try These Examples */}
                    <div className="mt-8">
                        <h3 className="text-gray-700 text-sm font-semibold mb-3 flex items-center">
                            <span className="text-cyan-600 text-xl mr-1">💡</span> Try these examples:
                        </h3>
                        <div className="space-y-3">
                            {examplePrompts.map((example, index) => (
                                <button
                                    key={index}
                                    onClick={() => setPrompt(example)}
                                    className="w-full text-left p-3 bg-stone-100 rounded-none text-gray-900 hover:bg-stone-200 transition-colors duration-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent border-2 border-black shadow-[2px_2px_0px_rgba(0,0,0,1)] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
                                >
                                    {example}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Panel - Output Gallery */}
                <div className="p-6 md:p-8 flex-1 bg-stone-50">
                    <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                        <span className="text-orange-600 text-3xl mr-2">🖼️</span> Output Gallery
                    </h2>
                    <p className="text-gray-700 text-sm mb-6">Your ultra-fast AI creations appear here instantly</p>

                    {/* Currently Generated Image */}
                    {generatedImageUrl ? (
                        <div ref={generatedImageRef} className="mb-8 p-4 bg-stone-50 rounded-none border-4 border-black text-center shadow-[6px_6px_0px_rgba(0,0,0,1)]">
                            <h3 className="text-lg font-bold text-gray-900 mb-3">Latest Creation:</h3>
                            <img src={generatedImageUrl} alt="AI generated artwork" className="w-full h-auto rounded-none shadow-md mb-4 max-w-sm mx-auto object-cover border-4 border-black" />
                            <div className="flex space-x-2">
                                <button
                                    onClick={saveImageToGallery}
                                    className="flex-1 bg-green-600 text-white font-bold py-2 px-4 rounded-none shadow-[4px_4px_0px_rgba(0,0,0,1)] hover:bg-green-700 transition-colors duration-100 disabled:opacity-50 disabled:cursor-not-allowed border-2 border-black active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
                                    disabled={!generatedImageUrl}
                                >
                                    {!db ? 'Demo: Save to Gallery' : 'Save to My Gallery'}
                                </button>
                                <button
                                    onClick={downloadImage}
                                    className="flex-1 bg-blue-600 text-white font-bold py-2 px-4 rounded-none shadow-[4px_4px_0px_rgba(0,0,0,1)] hover:bg-blue-700 transition-colors duration-100 border-2 border-black active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
                                >
                                    Download Image
                                </button>
                            </div>
                            
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-64 bg-stone-50 border-4 border-dashed border-black rounded-none text-gray-600 shadow-[6px_6px_0px_rgba(0,0,0,1)]">
                            <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-16 w-16 mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-lg font-bold text-gray-900">Ready for instant generation</p>
                            <p className="text-sm text-gray-600">Enter your prompt and unleash the power</p>
                        </div>
                    )}

                    {/* Saved Images Gallery */}
                    <div className="mt-8">
                        <h3 className="text-gray-700 text-sm font-semibold mb-3">Your Saved Creations:</h3>
                                                 {userId && (
                             <p className="text-gray-600 text-xs mb-4 p-1 border-2 border-black">
                                 Your unique session ID: <span className="font-mono break-all text-gray-900">{userId}</span>
                                 {!db && <span className="text-yellow-600 ml-2">(Demo Mode)</span>}
                             </p>
                         )}
                                                 {!db ? (
                             <div className="text-center text-gray-600 mt-6 p-4 border-4 border-dashed border-black rounded-none shadow-[4px_4px_0px_rgba(0,0,0,1)]">
                                 <p className="text-gray-700">Demo mode: Saved images would appear here when Firebase is configured.</p>
                             </div>
                         ) : savedImages.length === 0 ? (
                             <div className="text-center text-gray-600 mt-6 p-4 border-4 border-dashed border-black rounded-none shadow-[4px_4px_0px_rgba(0,0,0,1)]">
                                 <p className="text-gray-700">No saved images yet. Generate and save some to see them here!</p>
                             </div>
                         ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {savedImages.map((image) => (
                                    <div key={image.id} className="bg-stone-100 rounded-none shadow-[2px_2px_0px_rgba(0,0,0,1)] overflow-hidden border-2 border-black p-3">
                                        <img src={image.imageUrl} alt={`Saved artwork: ${image.prompt}`} className="w-full h-32 object-cover rounded-none mb-2 border-2 border-black" />
                                        <p className="text-sm text-gray-900 font-bold mb-1 line-clamp-2" title={image.prompt}>
                                            {image.prompt}
                                        </p>
                                        <p className="text-xs text-gray-700">
                                            Style: {image.aspectRatio || 'N/A'} | Mode: {image.mode || 'Text to Image'}
                                        </p>
                                        <p className="text-xs text-gray-600 mt-1">
                                            {image.timestamp ? new Date(image.timestamp.toDate()).toLocaleString() : 'Saving...'}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Feature Section (Seamless Block) */}
            <div className="w-full max-w-7xl mx-auto mt-12 mb-8 p-8 bg-[#f5ece0] rounded-none">
                <h2 className="text-4xl font-extrabold text-gray-900 text-center mb-4">Beyond Traditional</h2>
                <h3 className="text-2xl font-bold text-center text-gray-700 mb-2">New Standards for AI Image Editing with Gemini</h3>
                <p className="text-center text-gray-600 text-lg mb-10">Six breakthrough features powered by Google Gemini AI that redefine the possibilities of AI image editing</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    <FeatureCard
                        emoji="✏️"
                        title="Precision Inpainting"
                        description="Insert, repair, or transform image regions through natural language descriptions. Strong prompt fidelity ensures consistency in geometry and materials."
                        emojiBgColor="border-blue-600"
                        emojiBorderColor="text-blue-600"
                    />
                    <FeatureCard
                        emoji="🖼️"
                        title="Smart Canvas Extension"
                        description="Expand canvases while preserving perspective, lighting, and scene logic, suitable for product photos, concept art, and composite images."
                        emojiBgColor="border-green-600"
                        emojiBorderColor="text-green-600"
                    />
                    <FeatureCard
                        emoji="🌆"
                        title="Semantic Background Replacement"
                        description="Replace or relight backgrounds with style and palette control, from clean studio shots to cinematic scenes."
                        emojiBgColor="border-purple-700"
                        emojiBorderColor="text-purple-700"
                    />
                    <FeatureCard
                        emoji="⚡"
                        title="Maskless Local Edits"
                        description="No drawing tools required. Describe subjects, attributes, or boundary hints; Nano Banana automatically locates the edit range for you."
                        emojiBgColor="border-orange-600"
                        emojiBorderColor="text-orange-600"
                    />
                    <FeatureCard
                        emoji="❤️"
                        title="Reference-Guided Styling"
                        description="Maintain identity or artistic direction across edits with optional reference images, ensuring brand and character consistency."
                        emojiBgColor="border-pink-600"
                        emojiBorderColor="text-pink-600"
                    />
                    <FeatureCard
                        emoji="💬"
                        title="Conversational Workflow"
                        description="Step-by-step iteration: 'darker suit,' 'softer light,' 'move camera down'—the model tracks history for stable results."
                        emojiBgColor="border-cyan-600"
                        emojiBorderColor="text-cyan-600"
                    />
                </div>
            </div>

            {/* Price Plans Section (Seamless Block) */}
            <div className="w-full max-w-7xl mx-auto mt-12 mb-8 p-8 bg-[#f5ece0] rounded-none">
                <h2 className="text-4xl font-extrabold text-gray-900 text-center mb-4">Simple, <span className="text-purple-700">Transparent</span> Pricing</h2>
                <p className="text-center text-gray-700 text-lg mb-10">Choose the perfect plan for your creative needs. Start free, upgrade when you're ready.</p>

                <div className="flex justify-center flex-wrap gap-8">
                    <PlanCard
                        plan="Starter"
                        price="0"
                        description="Perfect for exploring AI image generation"
                        features={[
                            '10 monthly credits (refreshed each month)',
                            'Basic image quality (1024x1024)',
                            'Standard generation speed',
                            'Community support'
                        ]}
                        buttonText="Get Started Free"
                    />
                    <PlanCard
                        plan="Pro"
                        price="9.99"
                        description="Ideal for content creators and professionals"
                        features={[
                            '300 monthly credits (refreshed each month)',
                            'High-quality generation (up to 2048x2048)',
                            'Priority queue (2x faster)',
                            '24-hour email support',
                            'Advanced editing features',
                            'Commercial usage rights'
                        ]}
                        buttonText="Start Pro Plan"
                        isPopular={true}
                    />
                    <PlanCard
                        plan="Premium"
                        price="27.90"
                        description="Built for agencies and power users"
                        features={[
                            '900 monthly credits (refreshed each month)',
                            'Ultra-high quality generation',
                            'Fastest generation speed (priority queue)',
                            '2-hour dedicated support',
                            'API access',
                            'Custom integrations',
                            'Advanced analytics'
                        ]}
                        buttonText="Start Premium Plan"
                    />
                </div>

                <p className="text-center text-gray-600 text-xs mt-10">
                    All plans include access to our core features powered by Google Gemini AI. Upgrade or downgrade at any time.
                </p>
                <div className="text-center mt-4">
                    <button 
                        onClick={() => alert('📧 Contact us at support@adkitchen.com for custom enterprise plans!')}
                        className="text-blue-600 hover:text-blue-800 font-semibold text-sm underline"
                    >
                        Need a custom plan? Contact us
                    </button>
                </div>
                <div className="text-center text-gray-600 text-xs mt-2 flex justify-center items-center space-x-4">
                    <span className="flex items-center border-2 border-black p-1 bg-stone-100 shadow-[2px_2px_0px_rgba(0,0,0,1)]">
                        <svg className="w-3 h-3 text-green-700 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L4.414 9H16a1 1 0 110 2H4.414l5.293 5.293a1 1 0 010 1.414z" clipRule="evenodd"></path></svg> Secure payments powered by Stripe
                    </span>
                    <span className="flex items-center border-2 border-black p-1 bg-stone-100 shadow-[2px_2px_0px_rgba(0,0,0,1)]">
                        <svg className="w-3 h-3 text-green-700 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg> All major cards accepted
                    </span>
                    <span className="flex items-center border-2 border-black p-1 bg-stone-100 shadow-[2px_2px_0px_rgba(0,0,0,1)]">
                        <svg className="w-3 h-3 text-green-700 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg> Available worldwide
                    </span>
                </div>
            </div>

            {/* Price Comparison Calculator Section (Seamless Block) */}
            <div className="w-full max-w-4xl mx-auto mt-12 p-8 bg-blue-600 rounded-none">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white text-center mb-6">
                    Ad Kitchen vs Ad Creative AI Pricing 💸
                </h2>
                <p className="text-center text-blue-100 text-lg mb-8">
                    See how much you can save with Ad Kitchen compared to competitors!
                </p>

                <div className="mb-8">
                    <label htmlFor="generationsSlider" className="block text-blue-100 text-lg font-semibold mb-4">
                        Your expected number of generations per month?
                    </label>
                    <input
                        type="range"
                        id="generationsSlider"
                        min="1"
                        max="1000"
                        value={expectedGenerations}
                        onChange={(e) => setExpectedGenerations(parseInt(e.target.value))}
                        className="w-full h-2 bg-blue-300 rounded-none appearance-none cursor-pointer accent-blue-700 border-2 border-black"
                    />
                    <p className="text-center text-white text-2xl font-bold mt-4">
                        {expectedGenerations} generations per month
                    </p>
                </div>

                <div className="mb-10 text-center">
                    <label className="inline-flex items-center cursor-pointer p-2 bg-blue-700 border-2 border-black shadow-[4px_4px_0px_rgba(0,0,0,1)]">
                        <input
                            type="checkbox"
                            className="form-checkbox h-5 w-5 text-green-500 bg-gray-900 border-gray-600 rounded-none focus:ring-green-400"
                            checked={needsProFeatures}
                            onChange={(e) => setNeedsProFeatures(e.target.checked)}
                        />
                        <span className="ml-3 text-white text-lg font-bold">I need Pro Features</span>
                    </label>
                </div>

                <div className="grid grid-cols-2 gap-8 text-center mb-8">
                    <div className="bg-blue-700 p-6 rounded-none border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,1)]">
                        <h3 className="text-2xl font-bold text-white mb-4">Ad Kitchen</h3>
                        <p className="text-5xl font-extrabold text-white">
                            ${adKitchenCost.toFixed(2)} <span role="img" aria-label="Ad Kitchen emoji">{adKitchenEmoji}</span>
                        </p>
                    </div>
                    <div className="bg-blue-700 p-6 rounded-none border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,1)]">
                        <h3 className="text-2xl font-bold text-white mb-4">Ad Creative AI</h3>
                        <p className="text-5xl font-extrabold text-white">
                            ${adCreativeAICost.toFixed(2)} <span role="img" aria-label="Ad Creative AI emoji">{adCreativeAIEmoji}</span>
                        </p>
                    </div>
                </div>

                <p className="text-center text-white text-3xl font-extrabold mt-8 border-2 border-black p-2 shadow-[4px_4px_0px_rgba(0,0,0,1)]">
                    You save <span className="text-yellow-300">${savings.toFixed(2)}</span> per month! 🎉
                </p>
                <div className="text-center mt-4">
                    <button 
                        onClick={() => alert('🎯 Ready to start saving? Click any plan above to get started!')}
                        className="bg-yellow-400 text-black font-bold py-2 px-6 rounded-none border-2 border-black shadow-[4px_4px_0px_rgba(0,0,0,1)] hover:bg-yellow-300 transition-colors duration-100 active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
                    >
                        Start Saving Now!
                    </button>
                </div>
                <p className="text-center text-blue-100 text-sm mt-4">
                    *Savings based on comparable features and credit usage.
                </p>
            </div>
        </div>
    );
};

export default App;